import { clsx } from 'clsx';
import {
  Mail,
  Phone,
  MessageSquare,
  Ticket,
  DollarSign,
  TrendingUp,
  Users,
  FileText,
  StickyNote,
  Calendar,
} from 'lucide-react';
import { formatRelative } from '@/lib/format';

interface TimelineItem {
  id: string;
  type: string;
  title: string;
  summary: string;
  contactName?: string;
  timestamp: string;
}

interface TimelineProps {
  items: TimelineItem[];
  className?: string;
}

function getIcon(type: string) {
  if (type.includes('email')) return <Mail className="h-4 w-4" />;
  if (type.includes('call') || type.includes('voice')) return <Phone className="h-4 w-4" />;
  if (type.includes('sms') || type.includes('whatsapp') || type.includes('chat') || type.includes('message'))
    return <MessageSquare className="h-4 w-4" />;
  if (type.includes('ticket') || type.includes('support')) return <Ticket className="h-4 w-4" />;
  if (type.includes('invoice') || type.includes('payment') || type.includes('paid'))
    return <DollarSign className="h-4 w-4" />;
  if (type.includes('growth') || type.includes('signal')) return <TrendingUp className="h-4 w-4" />;
  if (type.includes('partnership') || type.includes('checkin')) return <Users className="h-4 w-4" />;
  if (type.includes('meeting') || type.includes('scheduled')) return <Calendar className="h-4 w-4" />;
  if (type.includes('note')) return <StickyNote className="h-4 w-4" />;
  return <FileText className="h-4 w-4" />;
}

function getColor(type: string) {
  if (type.includes('email')) return 'bg-blue-500/15 text-blue-400 border-blue-500/30';
  if (type.includes('call') || type.includes('voice')) return 'bg-purple-500/15 text-purple-400 border-purple-500/30';
  if (type.includes('sms') || type.includes('whatsapp') || type.includes('chat'))
    return 'bg-green-500/15 text-green-400 border-green-500/30';
  if (type.includes('ticket') || type.includes('support'))
    return 'bg-amber-500/15 text-amber-400 border-amber-500/30';
  if (type.includes('invoice') || type.includes('payment') || type.includes('paid'))
    return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
  if (type.includes('growth') || type.includes('signal'))
    return 'bg-orange-500/15 text-orange-400 border-orange-500/30';
  if (type.includes('partnership')) return 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30';
  return 'bg-surface-700 text-surface-400 border-surface-600';
}

export function Timeline({ items, className }: TimelineProps) {
  return (
    <div className={clsx('relative', className)}>
      <div className="absolute left-5 top-0 bottom-0 w-px bg-surface-700" />
      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.id} className="relative pl-12">
            <div
              className={clsx(
                'absolute left-2.5 top-1 flex items-center justify-center w-5 h-5 rounded-full border',
                getColor(item.type),
              )}
            >
              {getIcon(item.type)}
            </div>
            <div className="bg-surface-800/50 border border-surface-700/50 rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-surface-200 truncate">{item.title}</p>
                  <p className="text-xs text-surface-400 mt-0.5 line-clamp-2">{item.summary}</p>
                </div>
                <span className="text-xs text-surface-500 whitespace-nowrap flex-shrink-0">
                  {formatRelative(item.timestamp)}
                </span>
              </div>
              {item.contactName && (
                <p className="mt-1.5 text-xs text-surface-500">
                  Contact: <span className="text-surface-300">{item.contactName}</span>
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
